import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Divider,
  IconButton,
  Container,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { contactFormService } from './contactUs.service';
import type { ContactForm } from './contactUs.dto';
import moment from 'moment';
import { formatDateTime } from '@/helpers';

const ViewContactUs = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [contactForm, setContactForm] = useState<ContactForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info',
  });

  useEffect(() => {
    contactFormService.setLogoutCallback(logout);
  }, [logout]);

  useEffect(() => {
    const fetchContactForm = async () => {
      console.log('🔍 ViewContactUs: Fetching contact form, ID:', id);

      if (!token) {
        console.error('❌ ViewContactUs: No token');
        setError('Authentication required');
        setLoading(false);
        return;
      }

      if (!id) {
        console.error('❌ ViewContactUs: No ID provided');
        setError('Invalid ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        console.log('📡 ViewContactUs: Calling API...');
        const data = await contactFormService.getContactFormById(
          token,
          parseInt(id)
        );

        console.log('✅ ViewContactUs: Received data:', data);
        setContactForm(data);

        // Auto-mark as read if unread
        if (data.status === 0) {
          console.log('📧 ViewContactUs: Auto-marking as read...');
          setContactForm({ ...data, status: 1 });
        }
      } catch (err) {
        console.error('❌ ViewContactUs: Error fetching contact form:', err);
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch contact form';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchContactForm();
  }, [token, id]);

  const handleDelete = async () => {
    if (!token || !id) return;

    if (
      !window.confirm(
        'Are you sure you want to delete this contact form submission?'
      )
    ) {
      return;
    }

    try {
      await contactFormService.deleteContactForm(token, parseInt(id));
      setSnackbar({
        open: true,
        message: 'Contact deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/contact-us'), 1000);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to delete',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading contact form...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error || !contactForm) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <IconButton onClick={() => navigate('/contact-us')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Back to Contact Forms</Typography>
          </Box>
          <Alert severity="error">{error || 'Contact form not found'}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <Box display="flex" alignItems="center">
              <IconButton
                onClick={() => navigate('/contact-us')}
                sx={{ mr: 2 }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1">
                View Contact Form
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </Box>
        </Paper>

        <Card>
          <CardContent>
            {/* Name */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Name
              </Typography>
              <Typography variant="h6" gutterBottom>
                {contactForm.first_name} {contactForm.last_name}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Email */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {contactForm.email}
              </Typography>
            </Box>

            {contactForm.phone_number && (
              <>
                <Divider sx={{ my: 3 }} />
                <Box mb={3}>
                  <Typography variant="overline" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 1 }}>
                    {contactForm.phone_number}
                  </Typography>
                </Box>
              </>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Topic */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Topic
              </Typography>
              <Chip label={contactForm.topic} size="small" sx={{ mt: 1 }} />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Message */}
            <Box mb={3}>
              <Typography variant="overline" color="text.secondary">
                Message
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.6,
                }}
              >
                <Typography variant="body2">{contactForm.message}</Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Metadata */}
            <Box display="flex" gap={4} flexWrap="wrap">
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Submitted At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(contactForm.createdAt)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  Updated At
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(contactForm.updatedAt)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ViewContactUs;
